import get from 'lodash.get';
import groupBy from 'lodash.groupby';
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';

import { AppState } from 'services/types';
import { selectEntities } from 'services/entities/selectors';
import {
    FertilizerEntityId,
    FertilizerEntityIds
} from 'services/fertilizerEvents/types';
import { selectVarieties } from 'services/varieties/selectors';
import { makeSelectHarvestSchemaValue } from 'services/schemas/selectors';
import { getEntitiesOptionsWithVariety } from 'utils/events';
import { OwnProps } from './index';

const selectFertilizerEntityIds = (
    state: AppState,
    props: OwnProps
): FertilizerEntityIds => props.entityIds;

const selectFertilizerEntityId = (
    state: AppState,
    props: OwnProps
): FertilizerEntityId => props.entityId;

const selectEntityOptions = createDeepEqualSelector(
    selectFertilizerEntityIds,
    selectEntities,
    (entityIds, entities) =>
        entityIds.map((id) => {
            const entityName = get(entities, [id, 'name']);

            return {
                key: id,
                value: id,
                text: entityName
            };
        })
);

const selectEntitiesGroupedByVariety = createDeepEqualSelector(
    selectEntityOptions,
    selectEntities,
    selectVarieties,
    selectFertilizerEntityId,
    makeSelectHarvestSchemaValue('entityTypeId'),
    (
        entityOptions,
        entities,
        varieties,
        fertilizerEntityId,
        harvestEntityTypeId
    ) => {
        const innerEntities = groupBy(
            getEntitiesOptionsWithVariety(
                entityOptions,
                entities,
                varieties,
                fertilizerEntityId,
                harvestEntityTypeId
            ),
            'varietyName'
        );

        return Object.keys(innerEntities).reduce((prev, current) => {
            const entityNames = innerEntities[current]
                .map(({ entityName }) => entityName)
                .join(', ');

            return {
                ...prev,
                [current]: entityNames
            };
        }, {});
    }
);

const makeSelectEntitiesGroupedByVariety =
    (): typeof selectEntitiesGroupedByVariety => selectEntitiesGroupedByVariety;

export { selectFertilizerEntityIds, makeSelectEntitiesGroupedByVariety };
